import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Box,
  Tooltip
} from '@mui/material'
import {
  Close,
  ExitToApp,
  Computer,
  AccessTime,
  LocationOn
} from '@mui/icons-material'
import axios from 'axios'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

const SesionesActivasDialog = ({ open, onClose, usuario }) => {
  const [sesiones, setSesiones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    if (open && usuario) {
      loadSesiones()
    }
  }, [open, usuario])

  const loadSesiones = async () => {
    if (!usuario) return

    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get(`/api/auth/usuarios/${usuario.id}/sesiones`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSesiones(response.data)
    } catch (error) {
      setError('Error al cargar sesiones: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCerrarSesion = async (sesionId) => {
    if (!window.confirm('¿Está seguro de cerrar esta sesión? El usuario deberá volver a iniciar sesión.')) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/auth/sesiones/${sesionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Sesión cerrada exitosamente')
      loadSesiones()
    } catch (error) {
      setError('Error al cerrar sesión: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleCerrarTodasSesiones = async () => {
    if (!window.confirm(`¿Está seguro de cerrar todas las sesiones de ${usuario.nombre}? El usuario deberá volver a iniciar sesión en todos sus dispositivos.`)) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/auth/usuarios/${usuario.id}/sesiones`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Todas las sesiones cerradas exitosamente')
      loadSesiones()
      setTimeout(() => onClose(), 1500)
    } catch (error) {
      setError('Error al cerrar sesiones: ' + (error.response?.data?.detail || error.message))
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca'
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm:ss", { locale: es })
    } catch {
      return dateString
    }
  }

  const getBrowserInfo = (userAgent) => {
    if (!userAgent) return 'Desconocido'
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Edge'
    return 'Otro navegador'
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Sesiones Activas</Typography>
            <Typography variant="body2" color="text.secondary">
              {usuario?.nombre} ({usuario?.email})
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : sesiones.length === 0 ? (
          <Alert severity="info">
            No hay sesiones activas para este usuario
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Dispositivo/Navegador</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Última Actividad</TableCell>
                  <TableCell>Inicio de Sesión</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sesiones.map((sesion) => (
                  <TableRow key={sesion.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Computer fontSize="small" color="action" />
                        <Typography variant="body2">
                          {getBrowserInfo(sesion.user_agent)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2">
                          {sesion.ip_address || 'Desconocida'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(sesion.last_activity)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(sesion.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Cerrar sesión">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleCerrarSesion(sesion.id)}
                        >
                          <ExitToApp fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
        {sesiones.length > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleCerrarTodasSesiones}
            startIcon={<ExitToApp />}
          >
            Cerrar Todas las Sesiones
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default SesionesActivasDialog
